import React, { useEffect, useState } from "react";
import { Mail, X, Loader2 } from "lucide-react";
import { submitLead, trackEvent } from "@/lib/api";

const LEAD_KEY = "jh_lead_v1";

/** Returns the captured email (if any), else null. */
export function getCapturedEmail() {
  try {
    const raw = window.localStorage.getItem(LEAD_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v?.email || null;
  } catch {
    return null;
  }
}

function persistLead(email) {
  try {
    window.localStorage.setItem(LEAD_KEY, JSON.stringify({ email, t: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Singleton lead-gate modal. Imperative API via `requestLead({source, label, meta})`
 * returns a Promise that resolves with `{ok, email}` after the user submits
 * (or the gate is bypassed because we already have a captured email).
 */
let resolverRef = null;
let openerRef = null;

export function requestLead({ source = "download", label = "Unlock this download", meta = {} } = {}) {
  return new Promise((resolve) => {
    const existing = getCapturedEmail();
    if (existing) {
      // Already captured — fire-and-forget refresh ping but resolve right away.
      submitLead({ email: existing, source, ...meta }).catch(() => {});
      trackEvent("lead.skip", { source, ...meta });
      resolve({ ok: true, email: existing, cached: true });
      return;
    }
    resolverRef = resolve;
    if (openerRef) openerRef({ source, label, meta });
    else resolve({ ok: false, error: "no_modal_mounted" });
  });
}

export default function LeadGateModal() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [ctx, setCtx] = useState({ source: "download", label: "Unlock this download", meta: {} });
  const [err, setErr] = useState(null);

  useEffect(() => {
    openerRef = ({ source, label, meta }) => {
      setCtx({ source, label, meta });
      setEmail("");
      setErr(null);
      setOpen(true);
      trackEvent("lead.prompt.open", { source, ...meta });
    };
    return () => {
      openerRef = null;
    };
  }, []);

  const finish = (payload) => {
    setOpen(false);
    setBusy(false);
    if (resolverRef) {
      const r = resolverRef;
      resolverRef = null;
      r(payload);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!/.+@.+\..+/.test(value)) {
      setErr("Enter a valid email.");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await submitLead({ email: value, source: ctx.source, ...ctx.meta }).catch(() => ({ ok: false }));
    persistLead(value);
    trackEvent("lead.submit", { source: ctx.source, ok: !!res?.ok, ...ctx.meta });
    finish({ ok: true, email: value, response: res });
  };

  const onCancel = () => {
    trackEvent("lead.cancel", { source: ctx.source, ...ctx.meta });
    finish({ ok: false, cancelled: true });
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lead-gate-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0a0a]/70 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white border-4 border-[#0a0a0a] shadow-brutal"
      >
        <div className="flex items-center justify-between px-4 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
          <span>UNLOCK</span>
          <button type="button" onClick={onCancel} aria-label="Close" data-testid="lead-gate-close">
            <X size={12} strokeWidth={3} />
          </button>
        </div>
        <div className="p-6">
          <div className="font-display text-xl mb-1">{ctx.label}</div>
          <p className="font-mono-body text-sm text-[#0a0a0a]/70 mb-4">
            Drop your email and we&apos;ll unlock this — plus future downloads. No spam, unsubscribe anytime.
          </p>
          <label className="flex items-center gap-2 border-2 border-[#0a0a0a] px-3 py-2 bg-[#FDFBF7]">
            <Mail size={14} strokeWidth={3} className="text-[#0a0a0a]/55" />
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="lead-gate-email"
              className="flex-1 bg-transparent outline-none font-mono-body text-[14px]"
            />
          </label>
          {err && (
            <div className="mt-2 font-mono-body text-[12px] text-[#E10600]" data-testid="lead-gate-error">
              {err}
            </div>
          )}
          <div className="mt-5 flex items-center gap-3">
            <button
              type="submit"
              disabled={busy}
              data-testid="lead-gate-submit"
              className="hover-pop flex-1 inline-flex items-center justify-center gap-2 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-[12px] shadow-brutal-sm disabled:opacity-60"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : null}
              {busy ? "Unlocking…" : "Unlock"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              data-testid="lead-gate-skip"
              className="hover-pop font-accent text-[11px] text-[#0a0a0a]/55 hover:text-[#0a0a0a] px-2"
            >
              Maybe later
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
